// Conversation Analysis Service
// AI-powered post-conversation insights using OpenAI (via secure server-side function)

import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import {
  ConversationAnalysis,
  ConversationAnalysisRequest,
  ConversationAnalysisResult
} from '../types/conversationAnalysis.types';
import { CREDIT_COSTS } from '../types/credits.types';
import { spendCredits, canAfford } from './creditsService';

/**
 * Request AI analysis of a conversation
 * Cost: 5 credits
 * Now uses secure server-side Firebase Function
 */
export async function requestConversationAnalysis(
  userId: string,
  connectionId: string,
  phase: 'chat' | 'call' | 'video' | 'physical'
): Promise<ConversationAnalysisResult> {
  // 1. Check if user has enough credits
  const hasCredits = await canAfford(userId, CREDIT_COSTS.CONVERSATION_ANALYSIS);
  if (!hasCredits) {
    return {
      success: false,
      error: 'Onvoldoende credits. Je hebt 5 credits nodig voor een analyse.'
    };
  }
  
  // 2. Call secure server-side function (API key stays on server!)
  try {
    const analyzeConversation = httpsCallable<
      { connectionId: string; phase: string; userId: string },
      { success: boolean; analysis: any; otherUserName: string }
    >(functions, 'analyzeConversation');
    
    const result = await analyzeConversation({
      connectionId,
      phase,
      userId
    });
    
    if (!result.data.success) {
      return { success: false, error: 'Analyse kon niet worden uitgevoerd' };
    }
    
    const analysis = result.data.analysis;
    const otherUserName = result.data.otherUserName;
    
    // 3. Spend credits
    const spent = await spendCredits(
      userId,
      CREDIT_COSTS.CONVERSATION_ANALYSIS,
      'analysis',
      `Gesprek analyse: ${phase}`,
      { type: 'conversation', id: connectionId }
    );
    
    if (!spent) {
      return { success: false, error: 'Credits konden niet worden afgeschreven' };
    }
    
    // 4. Get connection data for otherUserId
    const connectionSnap = await getDoc(doc(db, 'connections', connectionId));
    const connectionData = connectionSnap.data();
    const otherUserId = connectionData?.participants?.find((id: string) => id !== userId);
    
    // 5. Save analysis to Firestore
    const analysisDoc = await addDoc(collection(db, 'conversationAnalyses'), {
      userId,
      connectionId,
      otherUserId,
      phase,
      summary: analysis.summary,
      keyPoints: analysis.keyPoints,
      actionItems: analysis.actionItems,
      topics: analysis.topics,
      sentiment: analysis.sentiment,
      entities: analysis.entities,
      strengths: analysis.strengths,
      growthPoints: analysis.growthPoints,
      isRead: false,
      createdAt: serverTimestamp(),
      creditCost: CREDIT_COSTS.CONVERSATION_ANALYSIS,
      aiModel: 'gpt-4o',
      tokensUsed: analysis.tokensUsed || 0
    });
    
    // 6. Create inbox notification
    await addDoc(collection(db, 'inbox'), {
      userId,
      type: 'summary',
      category: 'connection',
      title: 'Gesprek analyse klaar',
      message: `Je analyse van het ${phase} gesprek met ${otherUserName} is klaar.`,
      isRead: false,
      priority: 'medium',
      createdAt: serverTimestamp(),
      actionData: {
        actionType: 'view_summary',
        analysisId: analysisDoc.id
      }
    });
    
    const savedAnalysis: ConversationAnalysis = {
      id: analysisDoc.id,
      userId,
      connectionId,
      otherUserId: otherUserId || '',
      phase,
      ...analysis,
      isRead: false,
      createdAt: new Date(),
      creditCost: CREDIT_COSTS.CONVERSATION_ANALYSIS,
      aiModel: 'gpt-4o'
    };
    
    return {
      success: true,
      analysis: savedAnalysis
    };
    
  } catch (error: any) {
    console.error('[conversationAnalysisService] AI generation failed:', error);
    return {
      success: false,
      error: error.message || 'Analyse kon niet worden gegenereerd. Probeer het later opnieuw.'
    };
  }
}

/**
 * Get analyses for a user
 */
export async function getUserAnalyses(
  userId: string,
  limitCount: number = 10
): Promise<ConversationAnalysis[]> {
  const q = query(
    collection(db, 'conversationAnalyses'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate() ?? new Date()
  } as ConversationAnalysis));
}

/**
 * Mark analysis as read
 */
export async function markAnalysisAsRead(analysisId: string): Promise<void> {
  await updateDoc(doc(db, 'conversationAnalyses', analysisId), {
    isRead: true
  });
}

/**
 * Get single analysis
 */
export async function getAnalysis(analysisId: string): Promise<ConversationAnalysis | null> {
  const snap = await getDoc(doc(db, 'conversationAnalyses', analysisId));
  if (!snap.exists()) return null;
  
  return {
    id: snap.id,
    ...snap.data(),
    createdAt: snap.data().createdAt?.toDate() ?? new Date()
  } as ConversationAnalysis;
}
